/**
 * UserDropdown
 *
 * Authenticated user menu with profile/account links, admin link (if admin),
 * enterprise branding, and sign-out workflow. Uses AuthContext for cleanup.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { ChevronDown, Cog, LogOut, User, Crown, HelpCircle, Shield } from 'lucide-react';
import { useAuth } from '../../../context/auth';
import { useNamespace } from '../../../context/namespace';
import { auth } from '../../../firebase/firebase';
import { Dropdown } from '../../ui/dropdowns/Dropdown';
import { DropdownItem } from '../../ui/dropdowns/DropdownItem';
import { ButtonSpinner } from '../../ui/feedback/loading-spinner.jsx';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import { SERVER_URL } from '../../../api/core';

// Function to clear all AI-related localStorage items
const clearAIStorageData = () => {
  // CopyAnalyzer storage keys
  localStorage.removeItem('refrral_ad_copy');
  localStorage.removeItem('refrral_ad_analysis');
  
  // EasyWriter storage keys (if any are added in the future)
  localStorage.removeItem('refrral_easy_writer_data');
  
  // Clear all Stripe-related items that could cause redirect to success page
  localStorage.removeItem('stripeSessionId');
  localStorage.removeItem('stripe_session_id');
  localStorage.removeItem('stripe_checkout_initiated');
  localStorage.removeItem('stripe_checkout_auth_state');
  localStorage.removeItem('stripe_checkout_return_path');
  localStorage.removeItem('post_login_redirect');
  localStorage.removeItem('post_login_session_id');
  localStorage.removeItem('stripe_auth_uid');
  localStorage.removeItem('stripe_auth_email');
  localStorage.removeItem('stripe_auth_timestamp');
  localStorage.removeItem('auth_for_checkout');
  localStorage.removeItem('stripe_auto_login_needed');
  localStorage.removeItem('auth_redirect_state');
  localStorage.removeItem('auth_redirect_destination');
  sessionStorage.removeItem('in_stripe_checkout_flow');
  
  console.log('Cleared all AI-related localStorage items and Stripe-related data');
};

/* UserDropdown Component */
export default function UserDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [enterpriseData, setEnterpriseData] = useState(null);
  const [loadingEnterpriseData, setLoadingEnterpriseData] = useState(false);
  const { user, signOut } = useAuth();
  const { resetNamespaceContext } = useNamespace();
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  // Check if user is enterprise - ONLY by database flag, NOT by email domain
  const isEnterpriseUser = user?.isEnterpriseUser === true || user?.isEnterprise === true;

  // Debug logging for enterprise detection
  useEffect(() => {
    console.log('UserDropdown - Enterprise Detection Debug:', {
      userEmail: user?.email,
      isEnterpriseUserFlag: user?.isEnterpriseUser,
      isEnterpriseUser,
      enterpriseData,
      loadingEnterpriseData
    });
  }, [user?.email, user?.isEnterpriseUser, isEnterpriseUser, enterpriseData, loadingEnterpriseData]);

  // Get user's display name or email
  // Handle both formats: either firstName/lastName directly or displayName
  let displayName;
  let firstName;
  
  if (user?.firstName && user?.lastName) {
    // If firstName and lastName are directly available
    displayName = `${user.firstName} ${user.lastName}`;
    firstName = user.firstName;
  } else if (user?.displayName) {
    // If displayName is available (from Firebase Auth)
    displayName = user.displayName;
    firstName = displayName.split(' ')[0];
  } else {
    // Fallback to email or generic name
    displayName = user?.email?.split('@')[0] || 'User';
    firstName = displayName;
  }
  
  // Determine if user has a premium plan - updated to use planLevel instead of plan
  const isPremium = user?.planLevel === 'premium';

  // Check if user is the admin via Firebase custom claim
  const isAdmin = isUserAdmin(user);

  // Fetch enterprise data for enterprise users
  useEffect(() => {
    const fetchEnterpriseData = async () => {
      console.log('UserDropdown - fetchEnterpriseData called:', {
        isEnterpriseUser,
        userEmail: user?.email,
        hasEnterpriseData: !!enterpriseData,
        hasEnterpriseDataInAuth: !!user?.enterpriseData,
        loadingEnterpriseData
      });

      // First, check if we already have enterprise data from AuthContext
      if (user?.enterpriseData) {
        console.log('UserDropdown - Using enterprise data from AuthContext');
        setEnterpriseData(user.enterpriseData);
        setLoadingEnterpriseData(false);
        return;
      }

      if (!isEnterpriseUser || !user?.email || enterpriseData || loadingEnterpriseData) {
        console.log('UserDropdown - Skipping enterprise data fetch:', {
          isEnterpriseUser,
          hasEmail: !!user?.email,
          hasEnterpriseData: !!enterpriseData,
          loadingEnterpriseData
        });
        return;
      }

      console.log('UserDropdown - Starting enterprise data fetch...');
      setLoadingEnterpriseData(true);
      try {
        const emailDomain = user.email.split('@')[1];
        const url = `${SERVER_URL}/enterprise/check-domain/${emailDomain}`;
        console.log('UserDropdown - Fetching from:', url);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('UserDropdown - API Response:', {
          status: response.status,
          ok: response.ok
        });

        if (response.ok) {
          const data = await response.json();
          console.log('UserDropdown - Enterprise data received:', data);
          if (data.enterprise) {
            setEnterpriseData(data.enterprise);
            console.log('UserDropdown - Enterprise data set:', data.enterprise);
          } else {
            console.log('UserDropdown - No enterprise data in response');
          }
        } else {
          console.error('UserDropdown - Failed to fetch enterprise data:', response.status);
        }
      } catch (error) {
        console.error('UserDropdown - Error fetching enterprise data:', error);
      } finally {
        setLoadingEnterpriseData(false);
        console.log('UserDropdown - Enterprise data fetch completed');
      }
    };

    fetchEnterpriseData();
  }, [isEnterpriseUser, user?.email, user?.enterpriseData]);

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const closeDropdown = () => setIsOpen(false);

  const handleSignOut = async () => {
    // Prevent multiple sign out attempts
    if (isLoading || isSigningOut) return;
    
    // Immediately set loading states to prevent double-clicks
    setIsLoading(true);
    setIsSigningOut(true);
    console.log('Sign out process started from UserDropdown...');
    
    // Immediately close dropdown to provide feedback
    closeDropdown();
    
    try {
      // Reset namespace context
      resetNamespaceContext();
      
      // Clear namespace information and all AI-related localStorage data before signing out
      localStorage.removeItem('currentNamespace');
      clearAIStorageData();
      
      // Use AuthContext's signOut method for comprehensive cleanup
      // This will handle navigation automatically and set all necessary flags
      await signOut();
      
      // The AuthContext signOut method will handle navigation to '/' automatically
      console.log('UserDropdown: AuthContext signOut completed successfully');
      
    } catch (error) {
      console.error('Error during sign out process:', error);
      
      // If there's an error, still try to navigate home as fallback
      console.log('UserDropdown: Error occurred, using fallback navigation');
      window.location.href = '/';
    } finally {
      // Reset loading states (though this might not be reached due to navigation)
      setIsLoading(false);
      setIsSigningOut(false);
    }
  };

  // Plan badge component
  const PlanBadge = () => {
    if (isPremium) {
      return (
        <div className="ml-2 flex items-center gap-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-2 py-0.5">
          <Crown className="h-3 w-3 text-white" />
          <span className="text-xs font-medium text-white">Premium</span>
        </div>
      );
    }
    return null;
  };

  // Enterprise logo component for button
  const EnterpriseLogoButton = () => {
    if (!isEnterpriseUser || !enterpriseData?.logoUrl) {
      return null;
    }

    return (
      <img 
        src={enterpriseData.logoUrl} 
        alt={`${enterpriseData.name} logo`}
        className="w-4 h-4 rounded object-contain flex-shrink-0"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className={`flex items-center gap-2 p-2 text-gray-700 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 ${isOpen ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
      >
        <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        <span className="hidden md:flex md:items-center md:gap-2 font-medium">
          <span>{firstName}</span>
          <EnterpriseLogoButton />
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        buttonRef={buttonRef}
        className="absolute right-0 mt-2 w-[280px] rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-800 dark:bg-gray-900"
      >
        <div className="px-2 py-2 border-b border-gray-200 dark:border-gray-800">
          <div className="space-y-1">
            <div className="flex items-center">
              {isEnterpriseUser && (enterpriseData?.enterpriseImageUrl || enterpriseData?.enterpriseImage) && (
                <img 
                  src={enterpriseData.enterpriseImageUrl || enterpriseData.enterpriseImage} 
                  alt={`${enterpriseData.enterpriseName} logo`}
                  className="mr-2 w-5 h-5 rounded object-contain flex-shrink-0"
                  onError={(e) => {
                    console.log('UserDropdown - Enterprise logo failed to load:', enterpriseData.enterpriseImageUrl || enterpriseData.enterpriseImage);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => {
                    console.log('UserDropdown - Enterprise logo loaded successfully:', enterpriseData.enterpriseImageUrl || enterpriseData.enterpriseImage);
                  }}
                />
              )}
              <p className="font-medium text-gray-900 dark:text-white">{displayName}</p>
              <PlanBadge />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
            {/* Enterprise name display - replaces "Enterprise Account" */}
            {isEnterpriseUser && enterpriseData?.enterpriseName && (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {enterpriseData.enterpriseName}
              </p>
            )}
          </div>
        </div>

        <div className="py-2 border-b border-gray-200 dark:border-gray-800">
          <DropdownItem
            tag={Link}
            to="/app/profile"
            className="flex items-center gap-2 w-full px-2 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            onItemClick={closeDropdown}
          >
            <User className="w-4 h-4" />
            <span>Profile</span>
          </DropdownItem>

          <DropdownItem
            tag={Link}
            to="/app/account"
            className="flex items-center gap-2 w-full px-2 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            onItemClick={closeDropdown}
          >
            <Cog className="w-4 h-4" />
            <span>Account</span>
          </DropdownItem>
          
          <DropdownItem
            tag={Link}
            to="/app/support"
            className="flex items-center gap-2 w-full px-2 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            onItemClick={closeDropdown}
          >
            <HelpCircle className="w-4 h-4" />
            <span>Support</span>
          </DropdownItem>
          
          {/* Admin button - only shown to admin users */}
          {isAdmin && (
            <DropdownItem
              tag={Link}
              to="/app/private/admin"
              className="flex items-center gap-2 w-full px-2 py-2 text-sm text-violet-700 rounded-lg hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-900/20"
              onItemClick={closeDropdown}
            >
              <Shield className="w-4 h-4" />
              <span>Admin</span>
            </DropdownItem>
          )}
        </div>

        <div className="pt-2 border-t border-gray-200 dark:border-gray-800">
          <DropdownItem
            tag="button"
            disabled={isLoading || isSigningOut}
            onClick={handleSignOut}
            className={`flex items-center gap-2 w-full px-2 py-2 text-sm rounded-lg ${
              isLoading || isSigningOut
                ? 'text-gray-500 dark:text-gray-500 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
            }`}
          >
            {isLoading || isSigningOut ? (
              <>
                <ButtonSpinner color="gray-500 dark:gray-500" className="h-4 w-4" />
                <span>Signing out...</span>
              </>
            ) : (
              <>
                <LogOut className="w-4 h-4" />
                <span>Sign out</span>
              </>
            )}
          </DropdownItem>
        </div>
      </Dropdown>
    </div>
  );
}